"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import Check from "@/icons/status/seal-check.svg";
import Truck from "@/icons/ui/truck.svg";
import Oestra from "@/icons/products/product-oestra-r-colored-label.svg";
import Container from "~/componentsDS/Layout/Container";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Rating from "~/componentsND/Rating";
import {
    CarouselContent,
    CarouselItem,
    CarouselApi,
    CarouselPrevious,
    CarouselNext,
} from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { QuizCTA } from "~/components/features/quiz";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import Overline from "~/componentsDS/Typography/Overline";
import Accordion from "~/componentsDS/Accordion";
import Carousel, { carouselCompactArrowClasses } from "~/componentsDS/Carousel";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react/ssr";
import Marquee from "~/componentsDS/Marquee";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

const productImages = [
    {
        id: 1,
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775422137/new_design/tmp2cmxx9eu_2_2_1_jxpqn2.webp",
        srcMobile: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775422134/new_design/Frame_1171278223_fuj7qp.webp",
        thumb: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775422137/new_design/tmp2cmxx9eu_2_2_1_jxpqn2.webp",
        eventTitle: "Lifestyle",
    },
    {
        id: 2,
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531202/new_design/Group_45026_lqt4fj.webp",
        srcMobile: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531144/new_design/Frame_1171278221_plhnlr.webp",
        thumb: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531496/new_design/Frame_1171277877_ad5cyy.webp",
        eventTitle: "Clinical proof",
    },
    {
        id: 3,
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772530984/new_design/Group_45027_kbxpnv.webp",
        srcMobile: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531150/new_design/Frame_1171278224_irvqy4.webp",
        thumb: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531503/new_design/Frame_1171277880_ntcbcc.webp",
        eventTitle: "Patchwork",
    },
    {
        id: 4,
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772530973/new_design/tmp2cmxx9eu_2_2_2_kdwmym.webp",
        srcMobile: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531138/new_design/Frame_1171278222_ozvzbw.webp",
        thumb: "https://res.cloudinary.com/dirscuuhp/image/upload/v1772531491/new_design/tmp2cmxx9eu_2_2_2_vi62qo.webp",
        eventTitle: "Oestra product",
    },
];

const badges = [
    {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1773047993/face-cream/badge-1_ugspla.svg",
        alt: "U.S. 503A Licensed Pharmacy badge — Inner Balance compounding pharmacy certification",
        width: 76,
        height: 76,
    },
    {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1773047996/face-cream/badge-2_ajtz4g.svg",
        alt: "Active Ingredients Sourced from FDA-Inspected U.S. Facilities badge",
        width: 182,
        height: 76,
    },
    {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1773047993/face-cream/badge-3_alxcci.svg",
        alt: "Compounded in the USA badge — Inner Balance BodyMatched made in America",
        width: 76,
        height: 76,
    },
    {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1773047995/face-cream/badge-4_ui1jie.svg",
        alt: "Third-Party Tested for Purity, Potency and Consistency badge",
        width: 173,
        height: 76,
    },
];

const certifiedBadges = [
    {
        title: <>NABP<sup>®</sup></>,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775424800/new_design/nabp_zxn2gl.svg",
            alt: "NABP",
            width: 74,
            height: 24,
        },
    },
    {
        title: <>PCAB<sup>®</sup></>,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775424800/new_design/pcab_bzoymt.svg",
            alt: "PCAB",
            width: 64,
            height: 41,
        },
    },
    {
        title: <>LegitScript<sup>®</sup></>,
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1775424800/new_design/legit-script-certified_xsxxqd.webp",
            alt: "LegitScript",
            width: 37,
            height: 40,
        },
    },
]

const includes = [
    "Cancel anytime",
    "Free shipping",
    "20% off FullScript Supplements",
    "HSA/FSA Eligible",
    "Prescription treatment",
    "Unlimited access to healthcare experts",
];

const info = [
    {
        id: 1,
        question: "Ingredients",
        eventTitle: "Ingredients",
        answer: (
            <span className="block text-[16px]">
                <span className="block mb-4">Per pump: 3 mg estradiol + 100 mg micronized progesterone</span>
                <span className="block mb-4">- Bioidentical hormones identical to what your body naturally produces, supporting balanced, steady levels</span>
                <span className="block">- Delivered in a clean, pH-balanced cream base (MCT oil, hydrogenated vegan fat, glycerol monostearate, propoxyl stearate, ricinoleate, poloxamer 407, cetearyl ether) for consistent absorption</span>
            </span>
        ),
    },
    {
        id: 2,
        question: "Why it works",
        eventTitle: "Why it works",
        answer: (
            <span className="block text-[16px]">
                <span className="block mb-4">- Smarter absorption through vaginal delivery</span>
                <span className="block mb-4">- Hormones are absorbed directly into the bloodstream - bypassing the liver for more stable levels</span>
                <span className="block mb-4">- Higher absorption: ~100% vs. oral (10–20%) or topical (3–20%)</span>
                <span className="block">The result: more consistent hormone levels and more reliable symptom support</span>
            </span>
        ),
    },
    {
        id: 3,
        question: "Why it’s different",
        eventTitle: "Why it’s different",
        answer: (
            <span className="block text-[16px] space-y-4">
                <span className="block">- Body-matched formulation. Optimal estrogen-to-progesterone ratio to mimic the body’s natural state.</span>
                <span className="block">- Whole-body treatment. Not just for vaginal dryness — Oestra® supports mood, energy, sleep, metabolism, and more.</span>
                <span className="block">- Support whenever you need it. You’ll be guided by clinical experts who listen, adjust, and support every step of the way.</span>
                <span className="block">- Made for women by women. Founded by Dr. Sarah Daccarett, MD, after her own hormone journey.</span>
            </span>
        ),
    }
];

const ctaText = "Take the quiz";

const compactArrowClass = cn(carouselCompactArrowClasses, "shadow-none max-md:hidden");

const HeroSection = () => {
    const prevIndexRef = useRef<number | null>(null);
    const [api, setApi] = useState<CarouselApi>();
    const [current, setCurrent] = useState(0);
    const sendEvent = useTrackEvent();

    const sendGalleryEvent = useCallback(
        (item, index: number) => {
            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.PDP_INTERACTION,
                    widget: "pdp_gallery",
                    meta_data: {
                        image_title: item.eventTitle,
                        image_count: index + 1,
                    },
                },
            });
        },
        [sendEvent]
    );

    useEffect(() => {
        if (!api) {
            return;
        }

        const handleSelect = () => {
            const index = api.selectedScrollSnap();

            setCurrent(index);

            if (prevIndexRef.current !== index) {
                const item = productImages[index];
                if (item) {
                    sendGalleryEvent(item, index);
                }
                prevIndexRef.current = index;
            }
        };

        api.on("select", handleSelect);

        return () => {
            api.off("select", handleSelect);
        };
    }, [api, sendGalleryEvent]);

    const scrollTo = useCallback(
        (index: number) => {
            api?.scrollTo(index);
        },
        [api]
    );

    const renderQuestion = useCallback((question: string) => {
        return (
            <span className="block font-normal text-[18px]">{question}</span>
        );
    }, []);

    const handleFaqOpen = useCallback((item: any) => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.PDP_INTERACTION,
                widget: "pdp_value",
                meta_data: {
                    click_text: item.eventTitle,
                },
            },
        });
    }, [sendEvent]);

    return (
        <section className="pb-16 md:pb-[96px]">
            <Container className="max-md:px-0">
                <div className="flex flex-col lg:flex-row gap-4 md:gap-10 xl:gap-[115px]">
                    <div className="w-full">
                        <Carousel setApi={setApi} className="relative">
                            <CarouselContent className="max-md:-ml-2">
                                {productImages.map((item, index) => (
                                    <CarouselItem
                                        key={item.id}
                                        className="flex items-center basis-auto md:basis-full md:justify-center max-md:pl-2"
                                    >
                                        <div className="relative w-[352px] md:w-full pb-[75%] md:pb-[100%] rounded-3xl overflow-hidden">
                                            <Image
                                                src={item.srcMobile}
                                                alt="product"
                                                fill
                                                priority={index === 0}
                                                sizes="(max-width: 767px) 100vw, 0px"
                                                className="block md:hidden object-cover"
                                            />
                                            <Image
                                                src={item.src}
                                                alt="product"
                                                fill
                                                priority={index === 0}
                                                sizes="(min-width: 768px) 1200px"
                                                className="hidden md:block object-cover"
                                            />
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselPrevious className={cn("left-1", compactArrowClass)}>
                                <ArrowLeftIcon size={24} color="white" />
                            </CarouselPrevious>
                            <CarouselNext className={cn("right-1", compactArrowClass)}>
                                <ArrowRightIcon size={24} color="white" />
                            </CarouselNext>
                        </Carousel>

                        <div className="hidden md:flex justify-center w-full py-2 gap-[10px] mt-6">
                            {productImages.map((item, index) => (
                                <button
                                    key={item.id}
                                    aria-label={`${item.eventTitle} thumbnail`}
                                    aria-current={index === current ? "true" : undefined}
                                    className={cn("relative w-[75px] h-[75px] rounded-full", {
                                        "border-2 border-heroBlue": index === current,
                                    })}
                                    style={{
                                        backgroundImage: `url(${item.thumb})`,
                                        backgroundPosition: "center",
                                        backgroundSize: "cover",
                                    }}
                                    onClick={() => scrollTo(index)}
                                />
                            ))}
                        </div>

                        <div className="hidden md:block mt-6">
                            <Overline size="sm" className="text-primary500 text-center mb-6">Clinically Crafted. Quality Assured.</Overline>
                            <div className="flex shrink-0 items-center justify-center gap-2">
                                {badges.map(({src, alt, width, height}, index) => (
                                    <figure key={index} className="flex items-center">
                                        <Image src={src} alt={alt} width={width} height={height} quality={95} className="object-cover object-center" />
                                    </figure>
                                ))}
                            </div>
                            <div className="flex justify-center gap-2 flex-wrap mt-2">
                                {certifiedBadges.map(({ title, image: { src, alt, width, height } }, index) => (
                                    <div key={index} className="
                                        flex items-center gap-5 rounded-lg h-16 px-6
                                        bg-[linear-gradient(180deg,#E5E7EB_0%,#F3F4F6_100%)]
                                    ">
                                        <div className="flex flex-col items-center text-center">
                                            <span className="block font-suisseBP text-[29px] text-primary500 font-light leading-[1.1] -tracking-[0.01em]">{title}</span>
                                            <Paragraph className="text-[11.6px] text-primary400">Certified</Paragraph>
                                        </div>
                                        <Image src={src} alt={alt} width={width} height={height} quality={95} className="object-cover object-center" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col w-full lg:w-[400px] xl:w-[609px] shrink-0 max-md:px-6 text-primary500">
                        <div className="mb-4 order-1">
                            <Oestra className="w-[190px] h-auto mb-2 md:mb-3"/>

                            <Link href="#reviews" className="flex items-center gap-3 mb-4 md:mb-10 text-primary300 uppercase">
                                <Rating rating={5} />
                                <Paragraph size="xs" weight="500" className="leading-none tracking-[0.96px] pt-[2px]">4.9/5</Paragraph>
                                <Paragraph size="xs" weight="500" className="leading-none tracking-[0.96px] pt-[2px]">5,695 Reviews</Paragraph>
                            </Link>

                            <Paragraph size="lg" sizes={{md: "2xl"}} className="max-md:font-medium leading-[1.1] mb-1 md:mb-6">A prescription treatment with bioidentical estradiol and progesterone that delivers whole-body results.</Paragraph>
                            <Paragraph size="md" sizes={{md: "lg"}} className="mb-4 md:mb-10">Oestra® is a daily vaginal cream with bioidentical estrogen and progesterone. It restores whole-body hormone balance to improve sleep, mood, libido, and more — with better absorption and fewer side effects than pills or patches.</Paragraph>
                            <Paragraph size="lg" sizes={{md: "2xl"}} weight="500">
                                <span className="text-[16px] md:text-[18px] font-normal">From</span> $99.50/mo
                            </Paragraph>
                        </div>

                        <ul className="grid md:grid-cols-2 gap-3 md:gap-4 mb-8 md:mb-10 order-3 md:order-2">
                            {includes.map((include) => (
                                <li key={include} className="flex gap-1">
                                    <Check width={24} height={24} className="shrink-0" />
                                    <Paragraph size="lg" sizes={{md: "md"}} className="pt-[2px] md:pt-[3px] max-md:font-medium">{include}</Paragraph>
                                </li>
                            ))}
                        </ul>

                        <div className="mb-7 md:mb-8 order-2 md:order-3">
                            <QuizCTA
                                href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                                size="md"
                                className="w-full mb-[10px]"
                                eventData={{
                                    event_name: GTM_EVENT_NAMES.PDP_INTERACTION,
                                    widget: "pdp_main",
                                    meta_data: {
                                        click_text: ctaText,
                                    },
                                }}
                            >
                                {ctaText}
                            </QuizCTA>

                            <Paragraph size="sm" className="text-gray400 text-center mb-3 md:mb-4">Cancel Anytime</Paragraph>
                            <div className="flex justify-center items-center gap-3 text-primary400">
                                <Truck width={16} height={16} />
                                <Overline size="sm" className="pt-[2px]">Estimated delivery: 5 business days</Overline>
                            </div>
                        </div>

                        <div className="md:hidden order-4 -mx-6 mb-5">
                            <Overline size="sm" className="text-center text-primary500 mb-3">Clinically Crafted. Quality Assured.</Overline>

                            <Marquee allowDrag speed={1} direction="left" pauseOnHover={true}>
                                <div className="flex shrink-0 items-center">
                                    {certifiedBadges.map(({ title, image: { src, alt, width, height } }, index) => (
                                        <div key={index} className="
                                            flex items-center gap-5 rounded-lg h-16 px-6 shrink-0
                                            bg-[linear-gradient(180deg,#E5E7EB_0%,#F3F4F6_100%)]
                                            ml-2
                                        ">
                                            <div className="flex flex-col items-center text-center">
                                                <span className="block font-suisseBP text-[29px] text-primary500 font-light leading-[1.1] -tracking-[0.01em]">{title}</span>
                                                <Paragraph className="text-[11.6px] text-primary400">Certified</Paragraph>
                                            </div>
                                            <Image src={src} alt={alt} width={width} height={height} quality={95} className="object-cover object-center" />
                                        </div>
                                    ))}
                                </div>
                            </Marquee>
                        </div>

                        <div className="order-5">
                            <div className="relative mb-6">
                                <div className="absolute top-[-2px] w-full h-1 bg-pureWhite" />
                                <Accordion
                                    onOpen={handleFaqOpen}
                                    items={info}
                                    variant="compact"
                                    renderQuestion={renderQuestion}
                                />
                            </div>

                            <Paragraph size="xs" sizes={{md: "sm"}} className="text-gray300">
                                *Prescription-only product, prescribed by our board certified physicians. <br />
                                90 day supply replenished every 90 days.
                            </Paragraph>
                        </div>
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default HeroSection;
